import { FormControlLabel, Grid, Stack, Switch, Typography } from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import { useCallback, useMemo } from "react";
import { fromISO8601 } from "../../../../utils/iso8601";
import { CustomRepeatEvery } from "../../../event/components/custom-repeat-every";
import { CustomRepeatPeriod } from "../../../event/components/custom-repeat-period";
import { TimeZoneSelector } from "../../../schedule/components/time-zone-selector";

import type { ISO8601_TIME } from "../../../../utils/iso8601";
import type { CustomRepeatType } from "../../../event/hooks/use-number-of-repeats";
import type { SetSchedulePayload } from "../../reducer/actions";
import type { FollowUpSchemaType } from "../../reducer/schema";
import type { FC } from "react";

interface FollowUpScheduleScheduledProps {
  value: Extract<FollowUpSchemaType, { mode: "scheduled" }>["schedule"];
  onChange: (val: Extract<SetSchedulePayload, { mode: "scheduled" }>["schedule"]) => void;
  errors: {
    amount?: string;
    period?: string;
    time?: string;
    timezone?: string;
  };
  disabled: boolean;
}

export const FollowUpScheduleScheduled: FC<FollowUpScheduleScheduledProps> = ({
  value,
  onChange,
  errors,
  disabled,
}) => {
  const period = useMemo(() => {
    switch (value.period) {
      case "days":
        return "day";
      case "weeks":
        return "week";
      case "months":
        return "month";
      case "years":
        return "year";
      case null:
        return undefined;
    }
  }, [value.period]);

  const handlePeriodChange = useCallback(
    (val: CustomRepeatType["period"]) => {
      switch (val) {
        case "day":
          onChange({ ...value, period: "days" });
          break;
        case "week":
          onChange({ ...value, period: "weeks" });
          break;
        case "month":
          onChange({ ...value, period: "months" });
          break;
        case "year":
          onChange({ ...value, period: "years" });
          break;
        case undefined:
          onChange({ ...value, period: null });
          break;
      }
    },
    [onChange, value],
  );

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
        <Typography variant="body2">Send the Follow-Up</Typography>
        <CustomRepeatEvery
          label="Time"
          value={value.amount ?? undefined}
          onChange={(val) => onChange({ ...value, amount: val ?? null })}
          slotProps={{ htmlInput: { "data-analytics-id": "form-custom-repeat-every-input" } }}
          error={Boolean(errors.amount)}
          helperText={errors.amount}
          disabled={disabled}
        />
        <CustomRepeatPeriod
          label="Period"
          value={period}
          onChange={handlePeriodChange}
          slotProps={{ input: { "data-analytics-id": "custom-repeat-period-select" } }}
          error={Boolean(errors.period)}
          helperText={errors.period}
          disabled={disabled}
        />

        <Typography variant="body2">after receiving the Communication</Typography>
        {/* TODO: Add if condition not seen/etc... */}
      </Stack>
      <Grid container spacing={2} alignItems="center" justifyContent="flex-start">
        <Grid size={{ md: 12, lg: 2 }}>
          <TimePicker
            label="At"
            value={value.time ? fromISO8601(`1970-01-01T${value.time}`) : null}
            onChange={(newTime) => {
              const time = newTime
                ? `${newTime.getHours().toString().padStart(2, "0")}:${newTime.getMinutes().toString().padStart(2, "0")}`
                : null;

              // oxlint-disable-next-line typescript/no-unsafe-type-assertion
              onChange({ ...value, time: time as ISO8601_TIME });
            }}
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                size: "small",
                error: Boolean(errors.time),
                helperText: errors.time,
              },
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid size={{ md: 12, lg: 6 }}>
          <TimeZoneSelector
            value={value.timezone ?? null}
            onChangeTimeZone={(tz) => onChange({ ...value, timezone: tz })}
            targetChannel={false}
            disabled={disabled}
            sx={{ width: "100%" }}
            size="small"
            error={Boolean(errors.timezone)}
            helperText={errors.timezone}
          />
        </Grid>
        <Grid size={{ md: 12, lg: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={value.allow_weekends}
                onChange={(_, checked) => onChange({ ...value, allow_weekends: checked })}
                disabled={disabled}
              />
            }
            label={
              <Typography variant="body2">Allow sending on weekends (Saturday/Sunday)</Typography>
            }
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
